import React, { useState, useEffect, useMemo } from 'react';
import { useChallengesState } from 'hooks/useChallengesState';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { CHALLENGE_STATUS } from './state/constants';
import SelectCategory from 'pages/dashboard/challenges/SelectCategory';
import SelectChallenge from 'pages/dashboard/challenges/SelectChallenge';
import SelectOppRisk from 'pages/dashboard/challenges/SelectOppRisk';
import SelectActions from 'pages/dashboard/challenges/SelectActions';
import ChallengeMetrics from 'pages/dashboard/challenges/ChallengeMetrics';
import { DashboardEmptyState, RotatingWelcomeImage } from 'pages/dashboard/dashboard-helpers';
import { useTeam } from 'contexts/TeamContext';

// Move function outside component
const getAllSortedInstances = (instances, showArchived) => {
  const filteredInstances = Object.values(instances)
    .filter(instance => 
      instance.is_archived === showArchived && 
      instance.is_active === true
    );
  
  return filteredInstances.sort((a, b) => {
    const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return timeB - timeA;
  });
};

const Challenges = ({ showArchived = false }) => {
  const { 
    state, 
    archiveChallengeInstance, 
    addChallengeInstance, 
    fetchChallenges,
    loading,
    error 
  } = useChallengesState();
  const { currentTeamId } = useTeam();

  // Memoize the challenges list
  const allChallenges = useMemo(() => {
    if (!state.challenges?.instances) return [];
    const sorted = getAllSortedInstances(state.challenges.instances, showArchived);
    return sorted;
  }, [state.challenges?.instances, showArchived]);

  const isEmpty = useMemo(() => {
    return allChallenges.length === 0;
  }, [allChallenges]);

  const [collapsedChallenges, setCollapsedChallenges] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const toggleCollapse = (challengeId) => {
    setCollapsedChallenges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(challengeId)) {
        newSet.delete(challengeId);
      } else {
        newSet.add(challengeId);
      }
      return newSet;
    });
  };

  const handleArchiveClick = (challenge) => {
    archiveChallengeInstance(challenge.id);
  };

  const getActiveItem = (category_id, instance_id) => {
    const instance = state.challenges.instances[instance_id];
    const isDraft = instance?.status === CHALLENGE_STATUS.DRAFT;
    const opportunities = isDraft ? instance?.data?.opportunities || [] : [];
    const risks = isDraft ? instance?.data?.risks || [] : [];

    const activeItem = opportunities.find(item => item.is_active) || risks.find(item => item.is_active);

    if (activeItem) {
      return {
        type: opportunities.length > 0 ? 'opportunities' : 'risks',
        item: activeItem
      };
    }

    return null;
  };

  const handleSave = async (action) => {
    setIsSaving(true);
    await action();
    setIsSaving(false);
  };

  return (
    <ChallengesContainer>
      <ChallengesWrapper>

        {/* Map challenges if exist */}
        {allChallenges?.map((challenge) => {
          const activeItem = getActiveItem(challenge.category_id, challenge.id);
          return (
            <ChallengeRow 
              key={challenge.id} 
              data-challenge-id={challenge.id}
              data-testid="challenge-row"
            >
              <CategoryWrapper>
                <CollapseButton
                  onClick={(e) => {
                    if (!challenge.category_id || !challenge.id) return;
                    toggleCollapse(challenge.id);
                  }}
                  $isCollapsed={collapsedChallenges.has(challenge.id)}
                  $disabled={!challenge.category_id || !challenge.id}
                  title={!challenge.category_id || !challenge.id ?
                    "Select a category and challenge first" :
                    collapsedChallenges.has(challenge.id) ? "Expand" : "Collapse"
                  }
                >
                  <KeyboardArrowDownIcon />
                </CollapseButton>
                <SelectCategory challenge={challenge} />
                <SelectChallenge category_id={challenge.category_id} instance_id={challenge.id} />
                {challenge.id && (
                  <ChallengeMetrics
                    category_id={challenge.category_id}
                    instance_id={challenge.id}
                  />
                )}
                <ArchiveButton
                  onClick={() => handleArchiveClick(challenge)}
                  title={showArchived ? "Unarchive challenge" : "Archive challenge"}
                >
                  {showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                </ArchiveButton>

              </CategoryWrapper>
              {!collapsedChallenges.has(challenge.id) && (
                <>
                  {challenge.id && (
                    <SelectOppRisk category_id={challenge.category_id} instance_id={challenge.id} />
                  )}
                  {activeItem && (
                    <SelectActions
                      category_id={challenge.category_id}
                      instance_id={challenge.id}
                      item_type={activeItem.type}
                      item_id={activeItem.item.id}
                    />
                  )}
                </>
              )}
            </ChallengeRow>
          );
        })}

        {/* Empty state if no challenges exist */}
        {isEmpty && (
          <DashboardEmptyState
            showArchived={showArchived}
            archivedContent="No archived challenges yet."
            welcomeImage={<RotatingWelcomeImage />}
            title="Welcome to the Challenges tab – a place to brainstorm ideas & set actionable metrics for your project 🎯."
            subtitle="Reach for the clouds."
            actionButton={
              <AddButton 
                onClick={() => {
                  if (!currentTeamId) {
                    console.error('No active team found');
                    return;
                  }
                  // Create an initial challenge instance that will be updated with first brainstorm
                  addChallengeInstance({
                    name: 'New Challenge',
                    status: CHALLENGE_STATUS.NEW,
                    is_active: true,
                    created_at: new Date().toISOString(),
                    category_id: 'uncategorized',
                    is_archived: false,
                    team_id: currentTeamId,
                  });
                }}
                $shouldPulse={true}
              >
                Start Brainstorming!
              </AddButton>
            }
          />
        )}

      </ChallengesWrapper>
    </ChallengesContainer>
  );
};

const RoadmapContainer = styled.div.attrs({
  className: 'RoadmapContainer'
})`
  border-radius: 3px;
  background: black;
  color: white;
  position: relative;
  height: 100%;
`;

const ChallengesContainer = styled.div.attrs({
  className: 'ChallengesContainer'
})`
  display: flex;
  flex-direction: column;
  gap: 0px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const ChallengesWrapper = styled.div.attrs({
  className: 'ChallengesWrapper'
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  overflow-y: scroll;
  width: 100%;
  padding: 0px;
`;

const ChallengeRow = styled.div.attrs(props => ({
  className: 'ChallengeRow',
  'data-id': props.$challengeId,
  role: 'row'
}))`
  background: #1a1a1a;
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

const ChallengeHeader = styled.div.attrs({
  className: 'ChallengeHeader'
})`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 8px 5px 8px 5px; 
  user-select: none;
  gap: 15px;
  &:hover {
    background: rgba(255,255,255,0.05);
    border-radius: 3px;
  }
`;

const ChallengeNameSection = styled.div.attrs({
  className: 'ChallengeNameSection'
})`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  svg {
    font-size: 24px;
  }
`;

const CollapseIcon = styled(KeyboardArrowDownIcon).attrs({
  className: 'CollapseIcon'
})`
  transition: transform 0.3s ease;
  transform: ${props => props.$isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  color: #B2BEB5;
  cursor: pointer;
`;

const CategorySelector = styled.div.attrs({
  className: 'CategorySelector'
})`
  position: relative;
  display: flex;
  flex: 1;
`;

const CategoryDropdown = styled.div.attrs({
  className: 'CategoryDropdown'
})`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #2e2e2e;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  box-sizing: border-box;
  flex-wrap: nowrap;
  text-wrap: nowrap;
  width: 100%;
  &:hover {
    background: #333;
  }
`;

const IconWrapper = styled.span.attrs({
  className: 'IconWrapper'
})`
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
  font-size: 12px;
`;

const DropdownArrow = styled.div.attrs({
  className: 'DropdownArrow'
})`  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg) translateY(2px)' : 'rotate(0deg) translateY(2px)'};
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  svg {
    font-size: 18px;
  }
`;

const CategoriesList = styled.div.attrs({
  className: 'CategoriesList'
})`  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 3px;
  overflow: hidden;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CategoryItem = styled.div.attrs({
  className: 'CategoryItem'
})`
  padding: 10px 12px;
  color: black;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: #f5f5f5;
  }
`;



const Bottom = styled.div.attrs({
  className: 'Bottom'
})`  height: 50px;
  width: fit-content;
  position: absolute;
  bottom: -10px;
  left: 0;
  margin-bottom: 5px;
  z-index: 2;
`;

const ProgressWrapper = styled.div.attrs({
  className: "ProgressWrapper"
})`  position: absolute;
  bottom: 10px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  display: none;
`;

const ProgressBar = styled.div.attrs({
  className: "ProgressBar"
})`  width: 80px;
  height: 4px;
  background: #333;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div.attrs({
  className: "ProgressFill"
})`  height: 100%;
  width: ${props => props.width}%;
  background: #00da09;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.span.attrs({
  className: "ProgressLabel"
})`  font-size: 11px;
  color: #666;
`;

const DeleteBadge = styled.div.attrs({
  className: 'DeleteBadge'
})`  background: ${props => props.$isVisible ? '#2e2e2e' : 'transparent'};
  color: ${props => props.$isVisible ? 'orange' : 'transparent'};
  font-size: 17px;
  width: 30px;
  // height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-weight: 700;
  padding: 5px;
  box-sizing: border-box;
  flex-shrink: 0;
  margin-left: 5px;
  margin: -15px 0px;
  cursor: ${props => props.$isVisible ? 'pointer' : 'default'};
  svg {
    font-size: 20px;
  }
  &:hover {
    color: ${props => props.$isVisible ? '#ff4d4d' : 'transparent'};
  }
`;

const EmptyState = styled.div.attrs({
  className: 'EmptyState'
})`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 30px 20px;
  color: #666;
  font-size: 14px;
  width: 100%;
  text-align: left;
  background: #1a1a1a;
  border-radius: 5px;
  margin: 5px 0px;
  box-sizing: border-box;
  font-weight: 400;
  color: #c1c1c1;
  font-size: 16px;
  font-weight: 900;
  font-family: 'PT Sans', sans-serif;
  flex-direction: row;
  flex-wrap: wrap;
  div {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    height: 100%;
    gap: 10px;
  }
  span {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: white;
    font-weight: 300;
    border-radius: 5px;
    padding: 30px;
    background: color(srgb 0.17 0.17 0.17);
    gap: 15px;
    flex: 1;
  }
  i {
    text-decoration: underline;
  }
  img {
    border-radius: 5px;
    display: flex;
    height: 219px;
  }
`;

const AddButton = styled.button.attrs({
  className: 'AddButton'
})`
  align-self: flex-start;
  padding: 7px 12px 7px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 2px;
  background: #5000d0;
  color: white;
  border: none;
  cursor: pointer;
  margin-top: 15px;
  font-family: 'PT Sans', sans-serif;
  animation: ${props => props.$shouldPulse ? 'pulse 2s ease-out infinite' : 'none'};

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.2);
      font-weight: 900;
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
      font-weight: 700;
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
      font-weight: 900;
    }
  }

  &:hover {
    background: #4400b3;
    transition: background 0.3s ease;
    animation: none;
  }
`;

const CategoryWrapper = styled.div.attrs({
  className: 'CategoryWrapper'
})`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ArchiveButton = styled.button.attrs({
  className: 'ArchiveButton'
})`
  background: none;
  border: none;
  padding: 4px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  border-radius: 3px;

  &:hover {
    opacity: 1;
    color: #ff9800;
    background: rgba(255, 152, 0, 0.1);
  }

  svg {
    font-size: 20px;
  }
`;

const CollapseButton = styled.button.attrs({
  className: 'CollapseButton'
})`
  background: none;
  border: none;
  padding: 7px 4px;
  box-sizing: border-box;
  color: ${props => props.$disabled ? '#666' : 'white'};
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$disabled ? '0.3' : '.8'};
  border-radius: 2px;
  background: #262626;
  svg {
    font-size: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.$isCollapsed ? 'rotate(-180deg)' : 'rotate(0deg)'};
  }

  &:hover {
    opacity: ${props => props.$disabled ? '0.3' : '1'};
    color: ${props => props.$disabled ? '#666' : '#fff'};
    background: ${props => props.$disabled ? 'none' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const SaveIndicator = styled.div.attrs({
  className: 'SaveIndicator'
})`
  position: fixed;
  top: 10px;
  right: 10px;
  background: #ff9800;
  color: white;
  padding: 5px 10px;
  border-radius: 3px;
  font-size: 14px;
  z-index: 1000;
`;

const LoadingOverlay = styled.div.attrs({
  className: 'LoadingOverlay'
})`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1rem;
  text-align: center;
  z-index: 1000;
`;

const ErrorBanner = styled.div.attrs({
  className: 'ErrorBanner'
})`
  background: #ff4444;
  color: white;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorMessage = styled.span.attrs({
  className: 'ErrorMessage'
})`
  flex: 1;
`;

const RetryButton = styled.button.attrs({
  className: 'RetryButton'
})`
  background: white;
  color: #ff4444;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 1rem;
  
  &:hover {
    background: #f8f8f8;
  }
`;

export default Challenges;